import React from 'react';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap } from '@/store/slices/creativeJobs';

import { listPendingOf } from '../website/brand-settled';
import { useBrandSettled } from '../website/useBrandSettled';
import { deleteBannerVersion, purgeBannerVersion, restoreBannerVersion } from './creative-api';
import { listVideos, upsertVideoDraft, type SavedVideo } from './video-library-api';

export interface UseVideoLibrary {
  /** The CURRENT brand's video ads: empty until its first list lands (never the previous brand's after a switch). */
  videos: SavedVideo[];
  loading: boolean;
  /** The list for the CURRENT tenant has settled at least once (success OR failure), so an absent id is a
   *  real miss, not a not-yet-loaded one. Distinguishes "loading" from "loaded but empty" for deep links. */
  loaded: boolean;
  /** The current brand's list has answered (success OR failure), or no brand will resolve: a screen shows its
   *  skeleton until this. Unlike `loading`, a re-read of the same brand never turns it back off. */
  settled: boolean;
  /** The newest read for the CURRENT tenant failed (an unreachable backend, not an abort): an absent id is then
   *  unknown, not a miss. */
  listFailed: boolean;
  refresh: () => void;
  /** Soft-archive a saved ad (optimistic; rolls back by refetching on failure). */
  remove: (id: string) => Promise<boolean>;
  /** Recover an archived ad. */
  restore: (id: string) => Promise<boolean>;
  /** PERMANENTLY delete an archived ad (irreversible). */
  purge: (id: string) => Promise<boolean>;
  /** Optimistically merge fields into a cached ad (an in-editor name / storyboard edit), so a reopen or the
   *  listing card reads the fresh value immediately, instead of the pre-edit entity while the debounced server
   *  write is still in flight (which caused the editor to remount with a stale title). */
  patch: (id: string, fields: Partial<SavedVideo>) => void;
}

/**
 * Loads the brand's saved video ads and keeps them fresh: refetches whenever a storyboard job lands `done`
 * (the server has just persisted a new ad — no client-side save), and again when the brand (tenant) changes.
 * Delete/restore reuse the existing banner-version endpoints (a video creative archives/restores by id).
 */
/** The list handed out while the current brand has none of its own yet (stable, so consumers' memos hold). */
const NO_VIDEOS: SavedVideo[] = [];

export const useVideoLibrary = (tenantId?: number): UseVideoLibrary => {
  const [videos, setVideos] = React.useState<SavedVideo[]>([]);
  // The tenant `videos` were read for: they are handed out only for it (never the previous brand's after a switch).
  const [videosFor, setVideosFor] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const brandSettled = useBrandSettled();
  // The tenant whose list has settled (success OR failure), so `loaded` can tell "not fetched yet" from
  // "fetched and this id genuinely isn't there" — a deep-linked /videos/:id must not 404 before the fetch.
  const [loadedFor, setLoadedFor] = React.useState<number | undefined>(undefined);
  // The tenant whose newest read failed (cleared by a read that lands).
  const [failedFor, setFailedFor] = React.useState<number | undefined>(undefined);
  // Monotonic guard: a slow in-flight list for a previous tenant must never overwrite a newer one.
  const reqRef = React.useRef(0);
  // The brand this render is for. A load() kept by a closure from before a brand switch (a late save's refresh, an
  // archive rollback, a reconcile's re-read) must not start a read: it would bump reqRef past the current brand's read
  // in flight, which then never settles, and the grid would stay on its skeleton.
  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;

  const load = React.useCallback(
    (signal?: AbortSignal): void => {
      if (tenantId !== tenantRef.current) return; // a superseded brand's load (see tenantRef)
      if (!tenantId) {
        // No brand to read for (a brand still being created): forget the previous brand's read entirely, so coming
        // back to it is pending again (its skeleton) instead of settled over an emptied list; a read still in flight
        // can no longer settle it.
        reqRef.current += 1;
        setVideos([]);
        setVideosFor(undefined);
        setLoadedFor(undefined);
        setFailedFor(undefined);
        setLoading(false);
        return;
      }
      const seq = (reqRef.current += 1);
      setLoading(true);
      void listVideos(tenantId, signal)
        .then((rows) => {
          if (seq !== reqRef.current) return;
          setVideos(rows);
          setVideosFor(tenantId);
          setFailedFor(undefined);
        })
        .catch(() => {
          // A transient failure (or an abort) keeps the last list on screen; only a real failure is marked.
          if (seq === reqRef.current && !signal?.aborted) setFailedFor(tenantId);
        })
        .finally(() => {
          if (seq === reqRef.current) {
            setLoading(false);
            setLoadedFor(tenantId); // settle on failure too, so a failed list never hangs the editor on "Loading"
          }
        });
    },
    [tenantId],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Refetch when a storyboard job newly reaches `done`: the server persisted a video ad on completion, so the
  // library must pick it up without a page reload. Counting done storyboard jobs is a cheap change signal.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const doneCount = React.useMemo(
    () => Object.values(jobsById).filter((j) => j.kind === 'storyboard' && j.status === 'done').length,
    [jobsById],
  );
  const prevDoneRef = React.useRef(doneCount);
  React.useEffect(() => {
    if (doneCount > prevDoneRef.current) load();
    prevDoneRef.current = doneCount;
  }, [doneCount, load]);

  // Reconcile (FALLBACK): the worker now stamps video_url + the version history onto the linked entity at done
  // (SetVideoRendered). This only fires for an entity whose linked job finished before that backend shipped, or
  // whose job-link write raced the run; it writes video_url only (no version entry). Once per entity, and it
  // self-stops once video_url is set. Still what makes such a legacy ad show its video even if the tab was closed.
  const reconciledRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    // Only over rows read for THIS brand: right after a switch the state still holds the previous brand's rows.
    if (!tenantId || videosFor !== tenantId) return;
    videos.forEach((v) => {
      if (v.video_url || !v.job_id || reconciledRef.current.has(v.id)) return;
      const job = jobsById[v.job_id];
      if (job?.status === 'done' && job.result_url) {
        reconciledRef.current.add(v.id);
        void upsertVideoDraft(tenantId, {
          id: v.id,
          name: v.name,
          image_url: v.image_url,
          storyboard: v.storyboard,
          video_url: job.result_url,
          status: 'ready',
          job_id: v.job_id,
        }).then(() => load());
      }
    });
  }, [videos, videosFor, jobsById, tenantId, load]);

  const remove = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      // Optimistic: MARK archived (it leaves the live grid and appears in the Archived modal) rather than a
      // success-path refetch. A refetch here raced the server's archive write — it sometimes read the ad back
      // as still-live and resurrected it, so the archive looked like it did nothing (needed a second try) or
      // landed only after a delay. Trust the optimistic state on success; refetch only to roll back a failure.
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, archived: true } : v)));
      const ok = await deleteBannerVersion(tenantId, id);
      if (!ok) load(); // rollback: refetch the true state
      return ok;
    },
    [tenantId, load],
  );

  const restore = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      // Same optimistic pattern as `remove` (the mirror image): un-archive locally, refetch only on failure.
      setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, archived: false } : v)));
      const ok = await restoreBannerVersion(tenantId, id);
      if (!ok) load(); // rollback
      return ok;
    },
    [tenantId, load],
  );

  const purge = React.useCallback(
    async (id: string): Promise<boolean> => {
      if (!tenantId) return false;
      setVideos((prev) => prev.filter((v) => v.id !== id)); // optimistic (irreversible on the server)
      const ok = await purgeBannerVersion(tenantId, id);
      if (!ok) load(); // rollback: refetch the true state
      return ok;
    },
    [tenantId, load],
  );

  const patch = React.useCallback((id: string, fields: Partial<SavedVideo>): void => {
    setVideos((vs) => vs.map((v) => (v.id === id ? { ...v, ...fields } : v)));
  }, []);

  const pending = listPendingOf(tenantId, loadedFor, brandSettled);
  return {
    videos: tenantId !== undefined && videosFor === tenantId ? videos : NO_VIDEOS,
    // Loading until this brand's list has answered, not only while a read is in flight: before the brand (or the
    // first read) lands, an empty list would otherwise show its "none yet" state for a moment.
    loading: loading || pending,
    settled: !pending,
    loaded: tenantId !== undefined && loadedFor === tenantId,
    listFailed: tenantId !== undefined && failedFor === tenantId,
    refresh: () => load(),
    remove,
    restore,
    purge,
    patch,
  };
};
